/**
 * FR-030…032 · Popover chuyển tên miền nhanh — dựng lại đúng markup `.dsw`
 * của `domain-detail.html`: `.dsw > .dsw-btn + .dsw-pop(.dsw-head/.dsw-list/.dsw-foot)`.
 *
 * Điều hướng bằng ↑ ↓ Enter Esc, đóng khi click ra ngoài (docs/08 §8.7).
 */

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useT } from '../lib/i18n';
import { useFormat } from '../lib/format';
import type { SwitcherEntry } from '../lib/types';
import { Icon } from './Icon';

export function DomainSwitcher({ currentId }: { currentId: string }) {
  const { formatNumber } = useFormat();
  const t = useT();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [cursor, setCursor] = useState(0);

  const rootRef = useRef<HTMLSpanElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  /*
   * `aria-activedescendant` — để trình đọc màn hình THEO KỊP con trỏ (OQ-146).
   *
   * ↑ ↓ đổi `cursor` và ô đang trỏ đổi màu, nhưng tiêu điểm thật thì **không
   * rời ô tìm kiếm**. Với người dùng trình đọc màn hình, nhấn ↓ trước đây
   * không phát ra gì cả: họ không biết đang đứng ở tên miền nào cho tới lúc
   * bấm Enter — tức là chọn mù.
   *
   * Mẫu ARIA cho việc này là combobox + listbox: ô nhập giữ tiêu điểm và trỏ
   * tới tuỳ chọn đang hoạt động bằng `aria-activedescendant`, mỗi tuỳ chọn có
   * `id` riêng. `docs/08 §8.7` gọi đích danh popover này phải "điều hướng được
   * bằng ↑ ↓ Enter Esc" — điều hướng được mà không nghe thấy thì chưa đạt.
   */
  const listId = useId();
  const optionId = (index: number) => `${listId}-opt-${index}`;

  const listQuery = useQuery({
    queryKey: ['domains', 'switcher'],
    queryFn: () => api.get<SwitcherEntry[]>('/domains/switcher'),
    enabled: open,
    staleTime: 30_000,
  });

  const entries = useMemo(() => {
    const all = listQuery.data ?? [];
    const needle = term.trim().toLowerCase();
    return needle ? all.filter((entry) => entry.name.toLowerCase().includes(needle)) : all;
  }, [listQuery.data, term]);

  useEffect(() => {
    if (!open) return;
    searchRef.current?.focus();
    setCursor(0);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const choose = (id: string) => {
    setOpen(false);
    setTerm('');
    navigate(`/domains/${id}`);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      /*
       * Esc hai nấc: đang có chữ thì xoá chữ, ô rỗng rồi mới đóng bảng. Đóng
       * luôn khi người ta chỉ muốn gõ lại là bắt họ mở lại bảng từ đầu.
       */
      if (term) {
        setTerm('');
        setCursor(0);
        return;
      }
      setOpen(false);
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setCursor((value) => Math.min(entries.length - 1, value + 1));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setCursor((value) => Math.max(0, value - 1));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const entry = entries[cursor];
      if (entry) choose(entry.id);
    }
  };

  return (
    <span className="dsw" ref={rootRef} data-od-id="detail-domain-switcher">
      <button
        className="dsw-btn"
        type="button"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={t('detail.switch')}
        title={t('detail.switch')}
        onClick={() => setOpen((value) => !value)}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.9}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="m5.5 8 4.5 4.5L14.5 8" />
        </svg>
      </button>

      <div className="dsw-pop" hidden={!open} role="dialog" aria-label={t('detail.switch')}>
        {open ? (
          <>
            <div className="dsw-head">
              <div className="dsw-search">
                <input
                  ref={searchRef}
                  type="text"
                  role="combobox"
                  aria-expanded={open}
                  aria-controls={listId}
                  {...(entries[cursor] ? { 'aria-activedescendant': optionId(cursor) } : {})}

                  placeholder={t('detail.switchSearch')}
                  aria-label={t('detail.switchSearch')}
                  value={term}
                  onChange={(event) => {
                    setTerm(event.target.value);
                    setCursor(0);
                  }}
                  onKeyDown={onKeyDown}
                />
                {term ? (
                  <button
                    type="button"
                    className="search-clear"
                    aria-label={t('common.clearSearch')}
                    title={t('common.clearSearch')}
                    data-od-id="dsw-search-clear"
                    onClick={() => {
                      setTerm('');
                      setCursor(0);
                      searchRef.current?.focus();
                    }}
                  >
                    <Icon name="close" size={14} />
                  </button>
                ) : null}
              </div>
            </div>
            <div className="dsw-list" role="listbox" id={listId}>
              {listQuery.isPending ? (
                <div className="dsw-empty">{t('common.loading')}</div>
              ) : entries.length === 0 ? (
                <div className="dsw-empty">{t('detail.switchEmpty')}</div>
              ) : (
                entries.map((entry, index) => (
                  <button
                    key={entry.id}
                    id={optionId(index)}
                    type="button"
                    role="option"
                    aria-current={entry.id === currentId}
                    aria-selected={index === cursor}
                    onMouseEnter={() => setCursor(index)}
                    onClick={() => choose(entry.id)}
                    onKeyDown={onKeyDown}
                  >
                    <span className="zone-mark">
                      <Icon name="domains" size={15} />
                    </span>
                    <span className="who">
                      <b>{entry.name}</b>
                      <span>
                        {t(`domains.relation.${entry.relation}`)} ·{' '}
                        {formatNumber(entry.recordCount)} {t('domains.colRecords').toLowerCase()}
                      </span>
                    </span>
                  </button>
                ))
              )}
            </div>
            <div className="dsw-foot">
              <Link className="btn btn-sm btn-block" to="/domains" onClick={() => setOpen(false)}>
                {t('detail.switchAll')}
              </Link>
            </div>
          </>
        ) : null}
      </div>
    </span>
  );
}
